import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { RefreshCw, Copy, ExternalLink, QrCode, Code2, Link2, X, Ban, RotateCw, Bell, Timer } from "lucide-react";
import DOMPurify from "dompurify";

/** /platform/aanmeldlinks — beheerscherm voor de publieke aanmeldlinks per KDV.
 *  Elke KDV heeft één canonieke slug-based link. Superadmin ziet stats,
 *  kan link kopiëren, QR downloaden en embed-code genereren. */
export default function PlatformAanmeldlinksPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [q, setQ] = useState("");
  const [modal, setModal] = useState(null); // {mode: 'qr'|'embed', row}

  const publicBase = useMemo(() => (
    process.env.REACT_APP_PUBLIC_BASE_URL || window.location.origin
  ), []);

  const load = async () => {
    try {
      const r = await api.get("/platform/aanmeldlinks");
      setItems(r.data.items || []);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail || e.message)); }
    finally { setLoading(false); setRefreshing(false); }
  };

  const revoke = async (row) => {
    if (!window.confirm(`Weet je zeker dat je de aanmeldlink van ${row.name} wilt deactiveren?\n\nNieuwe aanmeldingen worden geweigerd tot je hem opnieuw activeert.`)) return;
    try {
      await api.post(`/platform/organizations/${row.organization_id}/aanmeldlink/revoke`);
      toast.success("Aanmeldlink gedeactiveerd");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail || e.message)); }
  };

  const regenerate = async (row) => {
    if (!window.confirm(`Weet je zeker dat je een NIEUWE aanmeldlink wilt genereren voor ${row.name}?\n\n⚠️ De oude link stopt DIRECT met werken. Update de website en QR-codes met de nieuwe link.`)) return;
    try {
      const r = await api.post(`/platform/organizations/${row.organization_id}/aanmeldlink/regenerate`);
      toast.success(`Nieuwe slug: ${r.data.slug}`);
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail || e.message)); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return items;
    return items.filter(i => (i.name || "").toLowerCase().includes(t) || (i.slug || "").toLowerCase().includes(t));
  }, [items, q]);

  const fullUrl = (slug) => `${publicBase}/aanmelden/${slug}`;

  const copy = (text, label = "URL") => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} gekopieerd`);
  };

  const embedCode = (row) => {
    const url = fullUrl(row.slug);
    return `<a href="${url}" target="_blank" rel="noopener" style="display:inline-block;padding:14px 28px;background:#6b8e5a;color:#fff;font-family:system-ui,sans-serif;font-weight:600;text-decoration:none;border-radius:8px;">Schrijf uw kind in bij ${row.name}</a>`;
  };

  const downloadQR = (row) => {
    const canvas = document.getElementById(`qr-canvas-${row.organization_id}`);
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `aanmeldlink-qr-${row.slug}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  return (
    <div data-testid="platform-aanmeldlinks">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900">Aanmeldlinks</h1>
          <p className="text-sm text-neutral-500 mt-1">Publieke inschrijflink per KDV — voor op de website of via e-mail te delen.</p>
        </div>
        <Button onClick={() => { setRefreshing(true); load(); }} disabled={refreshing} variant="outline" data-testid="aanmeldlinks-refresh">
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`} /> Verversen
        </Button>
      </div>

      <div className="mb-4">
        <Input placeholder="Zoek op naam of slug…" value={q} onChange={e => setQ(e.target.value)} className="max-w-md" data-testid="aanmeldlinks-search" />
      </div>

      {loading ? (
        <div className="text-center py-10 text-sm text-neutral-500">Laden…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-10 text-sm text-neutral-500" data-testid="aanmeldlinks-empty">Geen KDV's gevonden.</div>
      ) : (
        <div className="grid gap-3">
          {filtered.map(row => {
            const url = fullUrl(row.slug);
            return (
              <Card key={row.organization_id} className="p-4 bg-white border-neutral-200 hover:border-emerald-200 transition-colors" data-testid={`link-card-${row.organization_id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="text-lg font-semibold text-neutral-900">{row.name}</div>
                      {row.is_demo && <Badge className="bg-amber-100 text-amber-800 border border-amber-200 text-[10px] uppercase tracking-widest">Demo</Badge>}
                      {row.revoked
                        ? <Badge className="bg-red-50 text-red-700 border border-red-200 text-[10px] uppercase tracking-widest" data-testid={`link-revoked-${row.organization_id}`}>Inactief</Badge>
                        : <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] uppercase tracking-widest">Actief</Badge>}
                    </div>
                    <div className="mt-2 flex items-center gap-2 text-sm text-neutral-700 font-mono truncate" data-testid={`link-url-${row.organization_id}`}>
                      <Link2 className="h-4 w-4 text-neutral-400 shrink-0" />
                      <span className="truncate">{url}</span>
                    </div>
                    <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-2 text-xs text-neutral-600">
                      <div><span className="text-neutral-500">Aanmeldingen totaal:</span> <span className="font-semibold text-neutral-900" data-testid={`link-count-${row.organization_id}`}>{row.aanmeldingen_totaal}</span></div>
                      <div><span className="text-neutral-500">Laatste aanmelding:</span> <span className="font-mono">{row.laatste_aanmelding ? row.laatste_aanmelding.slice(0, 10) : "—"}</span></div>
                      <div><span className="text-neutral-500">Slug:</span> <span className="font-mono">{row.slug}</span></div>
                      {row.slug_rotation_months > 0 && (
                        <div className="col-span-2 md:col-span-3 flex items-center gap-1.5 text-amber-700" data-testid={`link-rotation-info-${row.organization_id}`}>
                          <Timer className="h-3.5 w-3.5" />
                          <span>Auto-rotatie elke <strong>{row.slug_rotation_months} mnd</strong> · volgende: <span className="font-mono">{row.slug_next_rotation_at ? row.slug_next_rotation_at.slice(0, 10) : "—"}</span></span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap shrink-0">
                    <Button size="sm" variant="outline" onClick={() => copy(url, "Link")} data-testid={`link-copy-${row.organization_id}`}>
                      <Copy className="h-3.5 w-3.5 mr-1" /> Kopiëren
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => window.open(url, "_blank")} data-testid={`link-preview-${row.organization_id}`}>
                      <ExternalLink className="h-3.5 w-3.5 mr-1" /> Preview
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setModal({ mode: "qr", row })} data-testid={`link-qr-${row.organization_id}`}>
                      <QrCode className="h-3.5 w-3.5 mr-1" /> QR
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setModal({ mode: "embed", row })} data-testid={`link-embed-${row.organization_id}`}>
                      <Code2 className="h-3.5 w-3.5 mr-1" /> Embed
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setModal({ mode: "notify", row })} data-testid={`link-notify-${row.organization_id}`}>
                      <Bell className="h-3.5 w-3.5 mr-1" /> Ontvangers
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setModal({ mode: "rotation", row })} data-testid={`link-rotation-${row.organization_id}`}>
                      <Timer className="h-3.5 w-3.5 mr-1" /> Rotatie
                    </Button>
                    {row.revoked ? (
                      <Button size="sm" onClick={() => regenerate(row)} className="text-white" style={{ background: "var(--sage)" }} data-testid={`link-regen-${row.organization_id}`}>
                        <RotateCw className="h-3.5 w-3.5 mr-1" /> Opnieuw activeren
                      </Button>
                    ) : (
                      <>
                        <Button size="sm" variant="outline" onClick={() => revoke(row)} className="border-red-200 text-red-700 hover:bg-red-50" data-testid={`link-revoke-${row.organization_id}`}>
                          <Ban className="h-3.5 w-3.5 mr-1" /> Deactiveren
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => regenerate(row)} className="border-amber-200 text-amber-800 hover:bg-amber-50" data-testid={`link-regen-${row.organization_id}`}>
                          <RotateCw className="h-3.5 w-3.5 mr-1" /> Opnieuw genereren
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setModal(null)}>
          <Card className="max-w-lg w-full p-6 bg-white" onClick={e => e.stopPropagation()} data-testid={`modal-${modal.mode}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="text-lg font-semibold text-neutral-900">
                {modal.mode === "qr" ? "QR-code" : modal.mode === "embed" ? "Embed-code voor website" : modal.mode === "notify" ? "Meldingsontvangers" : "Automatische link-rotatie"}
                <div className="text-xs text-neutral-500 mt-0.5 font-normal">{modal.row.name}</div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => setModal(null)}><X className="h-4 w-4" /></Button>
            </div>

            {modal.mode === "qr" && (
              <div className="text-center">
                <div className="inline-block p-4 bg-white border border-neutral-200 rounded-md">
                  <QRCodeCanvas
                    id={`qr-canvas-${modal.row.organization_id}`}
                    value={fullUrl(modal.row.slug)}
                    size={240}
                    level="M"
                    includeMargin
                    data-testid="modal-qr-canvas"
                  />
                </div>
                <div className="text-xs text-neutral-500 mt-3 font-mono break-all">{fullUrl(modal.row.slug)}</div>
                <div className="flex gap-2 justify-center mt-4">
                  <Button onClick={() => downloadQR(modal.row)} className="text-white" style={{ background: "var(--sage)" }} data-testid="modal-qr-download">Download PNG</Button>
                  <Button variant="outline" onClick={() => copy(fullUrl(modal.row.slug), "Link")}>Kopieer link</Button>
                </div>
              </div>
            )}

            {modal.mode === "embed" && (
              <div>
                <div className="text-sm text-neutral-600 mb-2">Plaats deze code op de website van de KDV:</div>
                <textarea
                  readOnly
                  className="w-full h-32 p-3 text-xs font-mono bg-neutral-50 border border-neutral-200 rounded-md"
                  value={embedCode(modal.row)}
                  data-testid="modal-embed-code"
                />
                <div className="mt-3 text-xs text-neutral-500">Preview:</div>
                <div className="mt-1 p-4 bg-neutral-50 border border-neutral-200 rounded-md text-center" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(embedCode(modal.row), { USE_PROFILES: { html: true } }) }} />
                <Button className="mt-4 text-white" style={{ background: "var(--sage)" }} onClick={() => copy(embedCode(modal.row), "Embed-code")} data-testid="modal-embed-copy">
                  <Copy className="h-4 w-4 mr-2" /> Embed-code kopiëren
                </Button>
              </div>
            )}

            {modal.mode === "notify" && <NotifyEditor org={modal.row} onClose={() => setModal(null)} />}

            {modal.mode === "rotation" && <RotationEditor org={modal.row} onClose={() => setModal(null)} onSaved={load} />}
          </Card>
        </div>
      )}
    </div>
  );
}

function NotifyEditor({ org, onClose }) {
  const [extras, setExtras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/platform/organizations/${org.organization_id}/notify-settings`);
        setExtras(r.data?.extras || []);
      } catch (e) { toast.error(formatApiError(e?.response?.data?.detail || e.message)); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, [org.organization_id]);

  const add = () => setExtras([...extras, { name: "", email: "", channel: "both", location_id: "" }]);
  const update = (i, k, v) => setExtras(extras.map((e, j) => j === i ? { ...e, [k]: v } : e));
  const remove = (i) => setExtras(extras.filter((_, j) => j !== i));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/platform/organizations/${org.organization_id}/notify-settings`, { extras });
      setExtras(r.data.extras || []);
      toast.success("Ontvangers opgeslagen");
      onClose && onClose();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-sm text-neutral-500 py-4">Laden…</div>;

  return (
    <div data-testid="notify-editor">
      <div className="text-sm text-neutral-600 mb-3">
        Extra ontvangers naast de directeur/beheerder. Push werkt alleen als de gebruiker een Webster-account heeft en pushmeldingen heeft toegestaan.
      </div>
      <div className="space-y-2 max-h-64 overflow-auto">
        {extras.length === 0 && <div className="text-xs text-neutral-500 italic">Geen extra ontvangers geconfigureerd.</div>}
        {extras.map((e, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`notify-row-${i}`}>
            <Input className="col-span-3" placeholder="Naam" value={e.name || ""} onChange={ev => update(i, "name", ev.target.value)} data-testid={`notify-name-${i}`} />
            <Input className="col-span-4" placeholder="email@voorbeeld.nl" value={e.email || ""} onChange={ev => update(i, "email", ev.target.value)} data-testid={`notify-email-${i}`} />
            <select
              className="col-span-3 h-9 px-2 rounded-md border border-neutral-200 text-sm bg-white"
              value={e.channel || "both"}
              onChange={ev => update(i, "channel", ev.target.value)}
              data-testid={`notify-channel-${i}`}
            >
              <option value="both">E-mail + push</option>
              <option value="email">Alleen e-mail</option>
              <option value="push">Alleen push</option>
            </select>
            <Button size="sm" variant="ghost" className="col-span-2 text-red-600 hover:bg-red-50" onClick={() => remove(i)} data-testid={`notify-remove-${i}`}>Verwijderen</Button>
          </div>
        ))}
      </div>
      <div className="flex justify-between mt-4">
        <Button variant="outline" onClick={add} data-testid="notify-add">+ Ontvanger toevoegen</Button>
        <Button onClick={save} disabled={saving} className="text-white" style={{ background: "var(--sage)" }} data-testid="notify-save">
          {saving ? "Opslaan…" : "Opslaan"}
        </Button>
      </div>
    </div>
  );
}

function RotationEditor({ org, onClose, onSaved }) {
  const [months, setMonths] = useState(org.slug_rotation_months || 0);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const m = Number.isFinite(+months) ? Math.max(0, Math.min(120, parseInt(+months, 10))) : 0;
      await api.put(`/platform/organizations/${org.organization_id}/aanmeldlink/rotation`, { months: m });
      toast.success(m > 0 ? `Automatische rotatie ingesteld op elke ${m} maand(en)` : "Automatische rotatie uitgeschakeld");
      onSaved && onSaved();
      onClose && onClose();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  const isOn = +months > 0;

  return (
    <div data-testid="rotation-editor">
      <div className="text-sm text-neutral-600 mb-4">
        Genereer automatisch een nieuwe slug (publieke aanmeldlink) na een vast aantal maanden. Verhoogt de veiligheid als de link gedeeld of gelekt is.
        <div className="text-xs text-amber-700 mt-1 bg-amber-50 border border-amber-200 rounded px-2 py-1">
          <strong>Let op:</strong> na rotatie werkt de oude link niet meer. Update dus je website en QR-codes na elke rotatie.
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <Label htmlFor="rot-months" className="text-sm">Rotatie-interval (maanden)</Label>
          <div className="flex items-center gap-2 mt-1">
            <Input
              id="rot-months"
              type="number"
              min={0}
              max={120}
              step={1}
              value={months}
              onChange={e => setMonths(e.target.value)}
              className="max-w-[140px]"
              data-testid="rotation-months-input"
              placeholder="0 = uit"
            />
            <span className="text-xs text-neutral-500">0 = automatische rotatie uit · max. 120 maanden</span>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {[3, 6, 12, 24].map(v => (
            <Button
              key={v}
              size="sm"
              variant={+months === v ? "default" : "outline"}
              onClick={() => setMonths(v)}
              data-testid={`rotation-preset-${v}`}
              className={+months === v ? "text-white" : ""}
              style={+months === v ? { background: "var(--sage)" } : {}}
            >{v} mnd</Button>
          ))}
        </div>

        {isOn && org.slug_last_rotated_at && (
          <div className="text-xs text-neutral-600 bg-neutral-50 rounded px-3 py-2 border border-neutral-200">
            Laatste rotatie / aanmaak: <span className="font-mono">{org.slug_last_rotated_at.slice(0, 10)}</span>
            <br />
            Volgende automatische rotatie: valt over <strong>{+months}</strong> maand(en) daarna. De scheduler check draait elk uur.
          </div>
        )}
      </div>

      <div className="flex justify-between mt-6">
        <Button variant="outline" onClick={onClose} data-testid="rotation-cancel">Annuleren</Button>
        <Button onClick={save} disabled={saving} className="text-white" style={{ background: "var(--sage)" }} data-testid="rotation-save">
          {saving ? "Opslaan…" : (isOn ? `Elke ${+months} mnd roteren` : "Rotatie uitzetten")}
        </Button>
      </div>
    </div>
  );
}

